import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import Icon from './Icon';

export interface DataTableColumn<T> {
  header: string;
  cell: (row: T) => React.ReactNode;
  /** Fixed width class, e.g. "tw-w-[200px]"; omit for the flexible column. */
  width?: string;
  /** Emphasised (primary text, semibold) instead of secondary text. */
  strong?: boolean;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Makes each row a button that opens the row (shows a chevron). */
  onRowClick?: (row: T) => void;
  /** Accessible label for a row's button, e.g. "Open claim CLM-2A90E4". */
  rowLabel?: (row: T) => string;
  className?: string;
}

/**
 * Dense list for desktop (Figma "Table header" + "Table row"), e.g. claim history or invoices.
 * Pages should show cards instead below 834px, as the design system specifies.
 */
function DataTable<T>({ columns, rows, rowKey, onRowClick, rowLabel, className }: DataTableProps<T>) {
  return (
    <div role="table" className={cn('tw-overflow-hidden tw-rounded-xl tw-border tw-border-ds-border-default', className)}>
      <div role="row" className="tw-flex tw-items-center tw-gap-4 tw-bg-ds-subtle tw-px-4 tw-py-2.5">
        {columns.map((col) => (
          <span
            key={col.header}
            role="columnheader"
            className={cn('tw-text-[11px] tw-font-semibold tw-leading-4 tw-tracking-[0.66px] tw-text-ds-text-tertiary', col.width ? cn('tw-shrink-0', col.width) : 'tw-min-w-0 tw-flex-1')}
          >
            {col.header}
          </span>
        ))}
        {onRowClick && <span aria-hidden className="tw-w-[18px] tw-shrink-0" />}
      </div>
      {rows.map((row) => {
        const cells = (
          <>
            {columns.map((col) => (
              <span
                key={col.header}
                role="cell"
                className={cn(
                  'tw-text-[14px] tw-leading-5',
                  col.strong ? 'tw-font-semibold tw-text-ds-text-primary' : 'tw-text-ds-text-secondary',
                  col.width ? cn('tw-shrink-0', col.width) : 'tw-min-w-0 tw-flex-1 tw-truncate',
                )}
              >
                {col.cell(row)}
              </span>
            ))}
            {onRowClick && <Icon icon={ChevronRight} className="tw-text-ds-text-tertiary" />}
          </>
        );
        const rowClass = 'tw-flex tw-w-full tw-items-center tw-gap-4 tw-border-t tw-border-ds-border-default tw-bg-ds-surface tw-px-4 tw-py-[14px] tw-text-left';
        return onRowClick ? (
          <button
            key={rowKey(row)}
            type="button"
            role="row"
            aria-label={rowLabel?.(row)}
            onClick={() => onRowClick(row)}
            className={cn(rowClass, 'tw-transition-colors hover:tw-bg-ds-subtle focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-ds-brand')}
          >
            {cells}
          </button>
        ) : (
          <div key={rowKey(row)} role="row" className={rowClass}>
            {cells}
          </div>
        );
      })}
    </div>
  );
}

export default DataTable;
